import { Box } from "@mui/joy";
import {
  addDays,
  eachDayOfInterval,
  eachWeekOfInterval,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  isSameWeek,
  max,
  min,
  parse,
  startOfMonth,
} from "date-fns";
import { useMeasure } from "react-use";

type BaseEvent = {
  startDate: string;
  endDate: string;
};

const weekDayLabels = {
  0: "Su",
  1: "Mo",
  2: "Tu",
  3: "We",
  4: "Th",
  5: "Fr",
  6: "Sa",
} as Record<number, string>;

const getWeekHeaderLabels = (weekStartsOn: number) => {
  const labels = [];
  for (let i = 0; i < 7; i++) {
    labels.push(weekDayLabels[(weekStartsOn + i) % 7]);
  }
  return labels;
};

type Props<Event extends BaseEvent> = {
  month: string;
  renderEvent: ({ event }: { event: Event }) => React.ReactNode;
  renderEmptyDay?: ({ date }: { date: string }) => React.ReactNode;
  renderExtraEvents: ({ date, events }: { date: string; events: Event[] }) => React.ReactNode;
  collapseHeightThreshold?: number;
  onDayContextMenu?: (e: React.MouseEvent, date: string) => void;
  events: Event[];
  weekStartsOn?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
};

const getDayClassNames = (day: Date, month: string) => {
  const classNames = [];
  if (isSameDay(day, new Date())) {
    classNames.push("today");
  } else if (day < new Date()) {
    classNames.push("past");
  }
  if (!isSameMonth(day, parse(month, "yyyy-MM", new Date()))) {
    classNames.push("other-month");
  }
  return classNames;
};

export default function Calendar<Event extends BaseEvent>({
  events,
  month,
  renderEvent,
  renderEmptyDay,
  collapseHeightThreshold,
  onDayContextMenu,
  renderExtraEvents,
  weekStartsOn = 1,
}: Props<Event>) {
  // const events = [
  //   {
  //     start: "2024-07-09",
  //     end: "2024-07-10",
  //   },
  //   {
  //     start: "2024-07-10",
  //     end: "2024-07-10",
  //   },
  //   {
  //     start: "2024-07-13",
  //     end: "2024-07-15",
  //   },
  //   {
  //     start: "2024-07-14",
  //     end: "2024-07-16",
  //   },
  // ];
  const monthDate = parse(month, "yyyy-MM", new Date());
  const monthStart = startOfMonth(monthDate);
  const monthEnd = endOfMonth(monthStart);
  const weekStarts = eachWeekOfInterval<Date>(
    { start: monthStart, end: monthEnd },
    {
      weekStartsOn,
    },
  );
  const weekDays = weekStarts.map((weekStart) =>
    eachDayOfInterval<Date>({ start: weekStart, end: addDays(weekStart, 6) }),
  );
  const eventsByDay: Record<string, Event[]> = {};
  const primaryDisplayEventsByRow: {
    event: Event;
    startDay: Date;
    endDay: Date;
  }[][] = weekStarts.map((_) => []);

  const extraDisplayEventsByDay: Record<string, Event[]> = {};
  events.forEach((event) => {
    eachDayOfInterval<Date>({
      start: parse(event.startDate, "yyyy-MM-dd", new Date()),
      end: parse(event.endDate, "yyyy-MM-dd", new Date()),
    }).forEach((day) => {
      const date = format(day, "yyyy-MM-dd");
      eventsByDay[date] = eventsByDay[date] || [];
      eventsByDay[date].push(event);
    });
    const eventWeeks = eachWeekOfInterval<Date>(
      {
        start: parse(event.startDate, "yyyy-MM-dd", new Date()),
        end: parse(event.endDate, "yyyy-MM-dd", new Date()),
      },
      {
        weekStartsOn,
      },
    );
    eventWeeks.forEach((eventWeek, _i) => {
      const weekIndex = weekStarts.findIndex((weekStart) =>
        isSameWeek(weekStart, eventWeek, { weekStartsOn }),
      );
      const weekStart = weekStarts[weekIndex];
      if (weekIndex === -1) {
        return;
      }
      const eventDaysInWeek = eachDayOfInterval<Date>({
        start: max([parse(event.startDate, "yyyy-MM-dd", new Date()), weekStart]),
        end: min([parse(event.endDate, "yyyy-MM-dd", new Date()), addDays(weekStart, 6)]),
      });
      const overlapsWithOtherEvents = eventDaysInWeek.some(
        (day) => eventsByDay[format(day, "yyyy-MM-dd")].length > 1,
      );
      if (overlapsWithOtherEvents) {
        eventDaysInWeek.forEach((day) => {
          const date = format(day, "yyyy-MM-dd");
          extraDisplayEventsByDay[date] = extraDisplayEventsByDay[date] || [];
          extraDisplayEventsByDay[date].push(event);
        });
        return;
      } else {
        primaryDisplayEventsByRow[weekIndex].push({
          event,
          startDay: max([parse(event.startDate, "yyyy-MM-dd", new Date()), weekStart]),
          endDay: min([parse(event.endDate, "yyyy-MM-dd", new Date()), addDays(weekStart, 6)]),
        });
      }
    });
  });

  const eventWeekRows: {
    event: Event;
    startDay: Date;
    endDay: Date;
  }[][] = weekStarts.map((_) => []);

  console.log("Event week rows", eventWeekRows);

  const [ref, { height }] = useMeasure();

  // Default the row height to a larger value so that we don't get a flash
  // on larger screens (since the height is 0 until the measure hook has run)
  const rowHeight = height ? height / weekStarts.length : 200;

  return (
    <Box
      ref={ref}
      sx={{
        display: "grid",
        gridTemplateRows: `max-content repeat(${weekStarts.length}, 1fr)`,
        ".event": {
          display: "flex",
          zIndex: 1,
          flexDirection: "column",
          pointerEvents: "none",
          "> *": {
            pointerEvents: "auto",
          },
        },
        ".week-row-header": {
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          borderBottom: "1px solid",
          borderColor: "neutral.200",
          ".week-header-cell": {
            display: "flex",
            justifyContent: "flex-start",
            paddingX: 1,
            fontSize: "sm",
            backgroundColor: "neutral.100",
            color: "neutral.600",
            alignItems: "center",
            fontWeight: "bold",
            borderRight: "1px solid",
            borderColor: "neutral.200",
            "&:last-of-type": {
              borderRight: "none",
            },
          },
        },
        ".week-row": {
          minHeight: "100px",
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gridTemplateRows: "max-content max-content 1fr",
          gridAutoFlow: "row",
          borderBottom: "1px solid",
          borderColor: "neutral.200",
          "&:last-of-type": {
            borderBottom: "none",
          },
          ".day": {
            gridRowStart: 1,
            gridRowEnd: 4,
            zIndex: 0,
            borderRight: "1px solid",
            borderColor: "neutral.200",
            "&.past, &.other-month": {
              backgroundColor: "#F4F4F5",
            },
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
          ".dayNumber": {
            gridRowStart: 1,
            gridRowEnd: 2,
            fontSize: "sm",
            fontWeight: "bold",
            zIndex: 4,
            width: 20,
            height: 20,
            margin: 0.25,
            lineHeight: 1,
            marginBottom: "-2px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderRadius: "100%",
            color: "neutral.500",
            "&.today": {
              backgroundImage: "var(--joy-palette-primary-solidGradientBg)",
              color: "white",
            },
          },
        },
        height: "100%",
      }}
    >
      <Box className="week-row-header">
        {getWeekHeaderLabels(weekStartsOn).map((label, i) => (
          <Box key={i} className="week-header-cell">
            {label}
          </Box>
        ))}
      </Box>
      {weekDays.map((week, i) => (
        <Box key={i} className="week-row">
          {week.map((day, j) => {
            const classNames = getDayClassNames(day, month);
            const date = format(day, "yyyy-MM-dd");
            const extraEvents = extraDisplayEventsByDay[date];
            return (
              <>
                <Box
                  key={j}
                  className={["day", ...classNames].join(" ")}
                  sx={{
                    gridColumnStart: j + 1,
                    gridColumnEnd: j + 2,
                    ...(j === 6 ? { borderRight: "none!important" } : {}),
                  }}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    onDayContextMenu?.(e, date);
                  }}
                >
                  {!eventsByDay[date]?.length && renderEmptyDay?.({ date })}
                </Box>
                <Box
                  className={["dayNumber", ...classNames].join(" ")}
                  sx={{
                    gridColumnStart: j + 1,
                    gridColumnEnd: j + 2,
                  }}
                >
                  {day.getDate()}
                </Box>
                {extraEvents && (
                  <Box
                    key={`${date}-extra-events`}
                    sx={{
                      gridColumnStart: j + 1,
                      gridColumnEnd: j + 2,
                      gridRowStart: 3,
                      gridRowEnd: 4,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "flex-start",
                      zIndex: 2,
                    }}
                  >
                    {renderExtraEvents({
                      date,
                      events: extraEvents,
                    })}
                    {/* +{extraEvents.length} more */}
                  </Box>
                )}
              </>
            );
          })}
          {primaryDisplayEventsByRow[i].map(({ event, startDay, endDay }) => {
            const startDayIndex = week.findIndex((d) => isSameDay(d, startDay));
            const endDayIndex = week.findIndex((d) => isSameDay(d, endDay));
            debugger;
            const daysHaveAnyOtherEvents = eachDayOfInterval<Date>({
              start: startDay,
              end: endDay,
            })
              .map((d) => eventsByDay[format(d, "yyyy-MM-dd")])
              .some((events) => events.length > 1);
            return (
              <Box
                key={event.startDate}
                className={`event ${rowHeight < (collapseHeightThreshold || 0) ? "collapsed" : ""}`}
                sx={{
                  gridColumnStart: startDayIndex + 1,
                  gridColumnEnd: endDayIndex + 2,
                  gridRowStart: 2,
                  gridRowEnd: !daysHaveAnyOtherEvents ? 4 : 3,
                }}
              >
                {renderEvent({ event })}
              </Box>
            );
          })}
        </Box>
      ))}
    </Box>
  );
}
